import { GroupForLmsQuery, GroupLessonType } from 'core/graphql';
import { isLessonOver } from 'services/LessonTimeService';
import { GroupTypes, LessonType } from 'components/types';
import { ICourseInfo } from 'molecules/CourseProgram/CourseInfo';
import { LessonSkill, ProgramLessonType } from 'components/types/lesson';

/**
 * Плоские поля ответа → форма, которую рисует занятие.
 *
 * 🔴 Спрос собирается в объект ЗДЕСЬ, а не разбирается в компоненте: полосу
 * рисует общий `SkillDemandLine`, и он принимает ту же `ISkillDemand`, что
 * приходит с карты навыков. Иначе одно и то же число жило бы в двух формах.
 *
 * Спрос без снимка (`demandVacancies` пуст) — это `null`, а не нулевой
 * объект: «не считали» и «ноль вакансий» на экране выглядят по-разному.
 */
const toLessonSkill = (skill: any): LessonSkill => ({
  id: skill.id,
  name: skill.name,
  slug: skill.slug,
  category: skill.category,
  iconUrl: skill.iconUrl,
  description: skill.description || '',
  unlockLevel: skill.unlockLevel ?? null,
  courseLevel: skill.courseLevel ?? null,
  demand:
    skill.demandVacancies === null || skill.demandVacancies === undefined
      ? null
      : { liveVacancies: skill.demandVacancies, band: skill.demandBand ?? 0 },
});

export const transformCourse = (data: GroupForLmsQuery | undefined): ICourseInfo => {
  if (data?.groupForLms?.__typename) {
    const groupType: GroupTypes = data.groupForLms.__typename as GroupTypes;

    // ТЗ 0040 доводка: навыки, которые даёт занятие. Раскладываем ПО ID —
    // номер занятия в системе считается тремя разными способами, и два из
    // них идут по дате.
    const planByLesson = new Map<string, { skills: LessonSkill[]; isOver: boolean }>();
    // @ts-ignore — поле есть только у студенческого типа группы
    (data.groupForLms.skillPlan ?? []).forEach((row: any) => {
      if (row?.lessonId) {
        planByLesson.set(String(row.lessonId), {
          skills: (row.skills ?? []).map(toLessonSkill),
          isOver: Boolean(row.isOver),
        });
      }
    });

    const result: ProgramLessonType[] =
      // @ts-ignore
      data.groupForLms.lessons?.reduce(
        (lessons: LessonType[], lessonData: GroupLessonType, index: number) => {
          // ТЗ 0100: «прошло» — по моменту конца, а не по дате.
          const isEnded = isLessonOver(lessonData);

          const accumulator: ProgramLessonType = {
            id: lessonData.id,
            name: lessonData.name,
            index: lessonData.index || index,
            isEnded,
            isActive: !isEnded,
            needToFinish: groupType === GroupTypes.GroupType ? lessonData.needToFinish : null,
            skills: planByLesson.get(String(lessonData.id))?.skills ?? [],
            skillsGranted: planByLesson.get(String(lessonData.id))?.isOver ?? false,
          };

          return [...lessons, accumulator];
        },
        [],
      ) || [];

    return {
      courseImageURL: data.groupForLms.course.courseIcon || '',
      courseName: data.groupForLms.course.name || '',
      groupName: data.groupForLms.name || '',
      studentsCount: data.groupForLms.studentsCount || 0,
      passedLessonsCount: data.groupForLms.passedLessonsCount || 0,
      lessons: result,
      lessonsCount: data.groupForLms.lessonsCount || 0,
      isFinished: data.groupForLms.statusFinished,
      isFree: data.groupForLms.isFree || false,
    };
  }

  return {
    courseImageURL: '',
    courseName: '',
    groupName: '',
    studentsCount: 0,
    passedLessonsCount: 0,
    lessons: [],
    lessonsCount: 0,
  };
};
